// anim.ts — the tween atom + a hitstop-aware clock. Effect #3 ("tween/easing helper") is really
// this file: overshoot + settle is the motion every other effect leans on.
//
// The clock matters because of hitstop (effect #1): when we freeze, we pause THIS clock, so every
// in-flight tween freezes together instead of each effect inventing its own pause. One clock, one
// freeze — the whole frame stops, which is what gives an impact weight.

let frozenUntil = 0;

/** Freeze all clock-driven tweens for `ms` (hitstop). Additive so rapid hits extend the freeze. */
export function freeze(ms: number): void {
  const now = performance.now();
  frozenUntil = Math.max(frozenUntil, now) + ms;
}

export function isFrozen(): boolean {
  return performance.now() < frozenUntil;
}

// ---- easings ----
export const easeOutBack =
  (overshoot = 1.70158) =>
  (t: number): number => {
    const c1 = overshoot;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  };

export const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

/**
 * Drive `onUpdate(eased)` from 0→1 over `durationMs`, respecting hitstop freezes. Resolves when
 * done. Uses the shared clock so a freeze pauses this tween with all the others.
 */
import { reduceMotion } from "./a11y";

export function tween(
  durationMs: number,
  ease: (t: number) => number,
  onUpdate: (v: number) => void,
): Promise<void> {
  // Reduced motion: jump straight to the settled end state, no animation.
  if (reduceMotion()) {
    onUpdate(1);
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    let elapsed = 0;
    let last = performance.now();
    function frame(now: number) {
      const dt = now - last;
      last = now;
      if (!isFrozen()) elapsed += dt; // frozen time doesn't advance the tween
      const t = Math.min(1, elapsed / durationMs);
      onUpdate(ease(t));
      if (t < 1) requestAnimationFrame(frame);
      else resolve();
    }
    requestAnimationFrame(frame);
  });
}
